import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
import { registerSW } from 'virtual:pwa-register'
import { AppErrorBoundary, initSentry } from '@/monitoring/sentry'

// Manejo de Service Worker y caché
if (import.meta.env.DEV && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((r) => {
      void r.unregister()
    })
  })
  if (typeof caches !== 'undefined') {
    caches.keys().then((keys) => {
      keys.forEach((k) => {
        void caches.delete(k)
      })
    })
  }
} else {
  // En producción: registrar actualización automática y recarga inmediata
  let refreshing = false
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true
        console.log('[PWA] Nueva versión activada. Recargando aplicación...')
        window.location.reload()
      }
    })
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      console.log('[PWA] Nueva versión disponible. Actualizando Service Worker...')
      updateSW(true)
    },
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Chequear actualización periódicamente cada 15 minutos
        setInterval(() => {
          registration.update()
        }, 15 * 60 * 1000)

        // Chequear actualización cuando el usuario regresa a la pestaña
        window.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update()
          }
        })
      }
    },
  })
}

initSentry()

function handleMetaOAuthPopupCallback(): boolean {
  if (typeof window === 'undefined') return false
  const urlParams = new URLSearchParams(window.location.search)
  const oauthCode = urlParams.get('code')
  const oauthState = urlParams.get('state')
  const oauthError = urlParams.get('error') || urlParams.get('error_description')

  if (!oauthState || !/^[a-f0-9]{64}$/i.test(oauthState) || (!oauthCode && !oauthError)) {
    return false
  }

  const redirectUri = `${window.location.origin}${window.location.pathname}`
  const payload = {
    type: 'IRIDIA_META_OAUTH_CALLBACK',
    code: oauthCode || '',
    state: oauthState,
    redirectUri,
    error: oauthError || undefined,
    timestamp: Date.now(),
  }

  try {
    localStorage.setItem('iridia_meta_oauth_callback', JSON.stringify(payload))
  } catch {
    // ignore storage errors
  }

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel('iridia_meta_oauth')
      bc.postMessage(payload)
      bc.close()
    }
  } catch {
    // ignore BroadcastChannel errors
  }

  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(payload, window.location.origin)
    }
  } catch {
    // ignore opener errors
  }

  const rootEl = document.getElementById('root')
  if (rootEl) {
    rootEl.innerHTML = oauthCode
      ? `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,sans-serif;background:#f8fafc;padding:24px;text-align:center;">
          <div style="background:white;padding:32px;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.08);max-width:400px;width:100%;">
            <div style="width:48px;height:48px;border-radius:50%;background:#d1fae5;color:#059669;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;font-size:24px;">✓</div>
            <h2 style="margin:0 0 8px;color:#0f172a;font-size:18px;font-weight:700;">¡Autorización de Meta completada!</h2>
            <p style="margin:0 0 20px;color:#475569;font-size:14px;line-height:1.5;">Conectando tu cuenta de WhatsApp en Iridia Chat. Esta ventana se cerrará automáticamente...</p>
            <button onclick="window.close()" style="background:#1877F2;color:white;border:none;padding:10px 20px;border-radius:8px;font-weight:600;cursor:pointer;font-size:14px;">Cerrar ventana</button>
          </div>
        </div>
      `
      : `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,sans-serif;background:#f8fafc;padding:24px;text-align:center;">
          <div style="background:white;padding:32px;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.08);max-width:400px;width:100%;">
            <h2 style="margin:0 0 8px;color:#dc2626;font-size:18px;font-weight:700;">Autorización cancelada</h2>
            <p style="margin:0 0 20px;color:#475569;font-size:14px;line-height:1.5;">Puedes cerrar esta ventana y volver a intentarlo desde Iridia Chat.</p>
            <button onclick="window.close()" style="background:#334155;color:white;border:none;padding:10px 20px;border-radius:8px;font-weight:600;cursor:pointer;font-size:14px;">Cerrar ventana</button>
          </div>
        </div>
      `
  }

  setTimeout(() => {
    try {
      window.close()
    } catch {
      // ignore
    }
  }, 400)

  return true
}

if (!handleMetaOAuthPopupCallback()) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>,
  )
}
